import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import HeadingProperties from "../FormElements/Heading/HeadingProperties";
import TextFieldProperties from "../FormElements/TextField/TextFieldProperties";
import ParagraphProperties from "../FormElements/Paragraph/ParagraphProperties";
import SignatureProperties from "../FormElements/Signature/SignatureProperties";
import RadioSelectionProperties from "../FormElements/RadioSelection/RadioSelectionProperties";
import FileUploadProperties from "../FormElements/FileUpload/FileUploadProperties";
import DatePickerComponentProperties from "../FormElements/DatePicker/DatePickerComponentProperties";
import CheckboxComponentProperties from "../FormElements/Checkbox/CheckboxComponentProperties";
import HorizontalContainerProperties from "../FormElements/HorizontalContainer/HorizontalContainerProperties";
import LogoComponentProperties from "../FormElements/Logo/LogoComponentPropertirs";
import PageBreakProperties from "../FormElements/PageBreak/PageBreakProperties";

const fieldOptions = [
  "SubcontractorName",
  "FirstName",
  "LastName",
  "Email",
  "PersonalNo",
  "Mobile",
  "Address",
  "City",
  "Zipcode",
  "RecipientRole",
  "ClientName",
  "ClientAddress",
];

const signaturOption = ["Client", "Subcontractor"];

const textFieldOption = ["Client", "Subcontractor"];

const PropertyEditor = ({
  elementId,
  elementType,
  elementProps,
  onPropsChange,
  elements,
}) => {
  const [selectedFieldOptions, setSelectedFieldOptions] = useState({});
  const [selectedTextFieldOptions, setSelectedTextFieldOptions] = useState({});
  useEffect(() => {
    const collectSelectedFields = (items) => {
      return items.reduce((acc, item) => {
        const { props: { selectedField } = {} } = item;
        if (selectedField) {
          acc[item.id] = selectedField;
        }
        if (item.items && item.items.length > 0) {
          const nestedFields = collectSelectedFields(item.items);
          return { ...acc, ...nestedFields };
        }
        return acc;
      }, {});
    };
    const collectSelectedTextFields = (items) => {
      return items.reduce((acc, item) => {
        const { props: { textFieldFor } = {} } = item;
        if (textFieldFor) {
          acc[item.id] = textFieldFor;
        }
        if (item.items && item.items.length > 0) {
          const nestedFields = collectSelectedTextFields(item.items);
          return { ...acc, ...nestedFields };
        }
        return acc;
      }, {});
    };

    const initialSelectedFields = collectSelectedFields(elements);
    delete initialSelectedFields[elementId];
    setSelectedFieldOptions(initialSelectedFields);
    const initialSelectedTextFields = collectSelectedTextFields(elements);
    delete initialSelectedTextFields[elementId];
    setSelectedTextFieldOptions(initialSelectedTextFields);
  }, [elements, elementId]);

  const renderPropertiesEditor = () => {
    switch (elementType) {
      case "heading":
        return (
          <HeadingProperties
            {...elementProps}
            elementId={elementId}
            onTextChange={(text, comments, trackChangesComments) =>
              onPropsChange({
                ...elementProps,
                text,
                comments,
                trackChangesComments,
              })
            }
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onBackgroundColorChange={(backgroundColor) =>
              onPropsChange({ ...elementProps, backgroundColor })
            }
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
          />
        );
      case "textField":
        return (
          <TextFieldProperties
            {...elementProps}
            key={elementId}
            elementId={elementId}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onColorChange={(color) => onPropsChange({ ...elementProps, color })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onSizeChange={(size) => onPropsChange({ ...elementProps, size })}
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            onSelectedFieldChange={(selectedField) =>
              onPropsChange({ ...elementProps, selectedField })
            }
            fieldOptions={fieldOptions.filter(
              (option) => !Object.values(selectedFieldOptions).includes(option)
            )}
            textfieldForOptions={textFieldOption}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onSelectedTextFieldForChange={(textFieldFor) =>
              onPropsChange({ ...elementProps, textFieldFor })
            }
          />
        );
      case "paragraph":
        return (
          <ParagraphProperties
            {...elementProps}
            elementId={elementId}
            onTextChange={(text, comments, trackChangesComments) =>
              onPropsChange({
                ...elementProps,
                text,
                comments,
                trackChangesComments,
              })
            }
            onColorChange={(color) => onPropsChange({ ...elementProps, color })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onBackgroundColorChange={(backgroundColor) =>
              onPropsChange({ ...elementProps, backgroundColor })
            }
            onPaddingChange={(padding) =>
              onPropsChange({ ...elementProps, padding })
            }
            onFontSizeChange={(fontSize) =>
              onPropsChange({ ...elementProps, fontSize })
            }
            onEditorInlineFieldsChange={(inlineFields) =>
              onPropsChange({ ...elementProps, inlineFields })
            }
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
          />
        );
      case "signature":
        return (
          <SignatureProperties
            {...elementProps}
            elementId={elementId}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            onSelectedFieldChange={(selectedField) =>
              onPropsChange({ ...elementProps, selectedField })
            }
            fieldOptions={signaturOption.filter(
              (option) => !Object.values(selectedFieldOptions).includes(option)
            )}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
          />
        );
      case "radioSelection":
        return (
          <RadioSelectionProperties
            {...elementProps}
            elementId={elementId}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onOptionsChange={(options) =>
              onPropsChange({ ...elementProps, options })
            }
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onSelectedFieldChange={(selectedField) =>
              onPropsChange({ ...elementProps, selectedField })
            }
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            fieldOptions={fieldOptions.filter(
              (option) => !Object.values(selectedFieldOptions).includes(option)
            )}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
          />
        );
      case "fileUpload":
        return (
          <FileUploadProperties
            {...elementProps}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            elementId={elementId}
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
          />
        );
      case "checkBox":
        return (
          <CheckboxComponentProperties
            {...elementProps}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            elementId={elementId}
            onSelectedFieldChange={(selectedField) =>
              onPropsChange({ ...elementProps, selectedField })
            }
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            fieldOptions={fieldOptions.filter(
              (option) => !Object.values(selectedFieldOptions).includes(option)
            )}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
          />
        );
      case "datePicker":
        return (
          <DatePickerComponentProperties
            {...elementProps}
            onLabelChange={(label) => onPropsChange({ ...elementProps, label })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            elementId={elementId}
            onSelectedFieldChange={(selectedField) =>
              onPropsChange({ ...elementProps, selectedField })
            }
            onRequiredFieldChange={(required) =>
              onPropsChange({ ...elementProps, required })
            }
            fieldOptions={fieldOptions.filter(
              (option) => !Object.values(selectedFieldOptions).includes(option)
            )}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
          />
        );
      case "horizontalContainer":
        return (
          <HorizontalContainerProperties
            {...elementProps}
            onRow1WidthChange={(width) =>
              onPropsChange({ ...elementProps, width })
            }
            onRow2WidthChange={(width) =>
              onPropsChange({ ...elementProps, width })
            }
            onRowHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onRowWidthChange={(width) =>
              onPropsChange({ ...elementProps, width })
            }
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            elementId={elementId}
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
          />
        );
      case "logo":
        return (
          <LogoComponentProperties
            {...elementProps}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            elementId={elementId}
          />
        );
      case "pageBreak":
        return (
          <PageBreakProperties
            {...elementProps}
            onRemove={() => onPropsChange({ ...elementProps, remove: true })}
            onWidthChange={(width) => onPropsChange({ ...elementProps, width })}
            onHeightChange={(height) =>
              onPropsChange({ ...elementProps, height })
            }
            onMarginLeftChange={(marginLeft) =>
              onPropsChange({ ...elementProps, marginLeft })
            }
            onMarginTopChange={(marginTop) =>
              onPropsChange({ ...elementProps, marginTop })
            }
            elementId={elementId}
          />
        );
      default:
        return <div>No properties to edit</div>;
    }
  };

  return <div>{renderPropertiesEditor()}</div>;
};

PropertyEditor.propTypes = {
  elementType: PropTypes.string.isRequired,
  elementProps: PropTypes.object,
  onPropsChange: PropTypes.func.isRequired,
};

export default PropertyEditor;
